import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@apollo/client';
import useStyles from 'isomorphic-style-loader/useStyles';
import dayjs from 'dayjs';
import { Virtuoso } from 'react-virtuoso';

// app modules: components
import Advert from 'components/Advert';
import Button from 'components/Button';
import ErrorRouter from 'components/ErrorRouter';
import ExcerptItem, { ExcerptItemComponent } from 'components/ExcerptItem';
import Heading from 'components/Heading';
import Link from 'components/Link';
import Offline from 'components/Offline';
import PullToRefresh from 'components/PullToRefresh';

// app modules: services
import history from 'services/history';
import { isApp } from 'services/environment';
import route from 'services/route';

// app modules: constants
import { EZOIC_INFINITE_SCROLL_PLACEMENT_IDS } from 'constants/index';

// app: assets
import CssButton from 'components/Button/Button.scss';
import CssBlogPosts from './BlogPosts.scss';

const GET_POSTS = gql`
  query GetBlogPosts(
    $limit: Int
    $offset: Int
    $isbn: String
    $personId: String
    $category: String
    $tags: [String]
  ) {
    BlogPosts(
      limit: $limit
      offset: $offset
      isbn: $isbn
      personId: $personId
      category: $category
      tags: $tags
    ) {
      asset_url
      description
      excerpt
      id
      title
      url_id
    }
  }
`;

function BlogPosts(props) {
  const {
    allowHtmlDescription,
    category,
    className,
    gutter,
    headingClassName,
    headingWithDash,
    headingLevel,
    headingLink,
    hideHeading,
    hideIfEmpty,
    infiniteScroll,
    isbn,
    itemProps,
    limit,
    offset,
    personId,
    isPullable,
    showMoreButton,
    skeletonItems,
    tags,
  } = props;
  useStyles(CssBlogPosts, CssButton);

  const initialIndex =
    history.location.state && history.location.state.listIndex
      ? history.location.state.listIndex
      : 0;
  const initialLimit = initialIndex ? Math.ceil(initialIndex / limit) * limit : limit;

  const { loading, error, data, fetchMore, refetch } = useQuery(GET_POSTS, {
    variables: { offset, limit: initialLimit, isbn, personId, tags, category },
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
  });
  const [hasMore, setHasMore] = useState(infiniteScroll);

  const loadMore = () =>
    fetchMore({
      variables: {
        limit,
        offset: data ? data.BlogPosts.length : 0,
        tags,
        category,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.BlogPosts || !fetchMoreResult.BlogPosts.length) {
          setHasMore(false);
          return prev;
        }

        const isDuplicatePage = fetchMoreResult.BlogPosts.some(post =>
          prev.BlogPosts.some(prevPost => prevPost.id === post.id),
        );
        if (isDuplicatePage) {
          console.warn('Duplicate page of blogs ignored');
          return prev;
        }
        return { ...prev, BlogPosts: [...prev.BlogPosts, ...fetchMoreResult.BlogPosts] };
      },
    });

  const itemRenderer = (index, post) => (
    <div
      key={post.id || `blog-skeleton-item-${index}`}
      // Do not add margin as it screws up the Virtuoso's height calculations
      className="padding-bottom-20"
    >
      <ExcerptItem
        title={post.title}
        headingLevel={headingLevel + 1}
        onClick={() => {
          history.replace({ ...history.location, state: { listIndex: index } });
        }}
        description={allowHtmlDescription ? post.excerpt : post.description}
        gutter={gutter}
        link={route.blogPost(post.url_id)}
        image={post.asset_url}
        imageOrientation="h"
        dangerouslyRenderHtmlDescription
        loading={!post.title}
        floatRight
        {...itemProps}
      />

      {(index + 1) % 5 === 0 ? (
        <React.Fragment>
          <Advert
            slotName="search-leaderboard"
            format="leaderboard"
            fullWidthResponsive={false}
            className="margin-bottom-20"
            ezoicPlaceholderId={
              EZOIC_INFINITE_SCROLL_PLACEMENT_IDS[
                index % EZOIC_INFINITE_SCROLL_PLACEMENT_IDS.length
              ]
            }
            lazy
          />
        </React.Fragment>
      ) : null}
    </div>
  );

  const handleUserRefresh = () =>
    // For a user initiated refresh we send a cache buster: the timestamp rounded to the nearest minute.
    refetch({
      _freshness: dayjs()
        .startOf('minute')
        .valueOf(),
    });

  const Component = isPullable ? PullToRefresh : 'div';
  const componentProps = isPullable ? { onRefresh: handleUserRefresh } : {};

  let list;
  if (infiniteScroll) {
    // Render a virtualized list
    list = (
      <Virtuoso
        useWindowScroll
        style={{ height: '100vh' }}
        initialTopMostItemIndex={Math.max(0, initialIndex)}
        data={data && data.BlogPosts ? data.BlogPosts : []}
        totalCount={data && data.BlogPosts ? data.BlogPosts.length : 0}
        itemContent={itemRenderer}
        endReached={loadMore}
        overscan={200}
        components={{
          Footer: () =>
            Array(hasMore ? skeletonItems : 0)
              .fill()
              .map((_, i) => itemRenderer(i, {})),
        }}
      />
    );
  } else {
    // Render the items directly
    list = [];
    const listLength = data && data.BlogPosts ? data.BlogPosts.length : limit;
    for (let i = 0; i < listLength; i += 1) {
      if (data && data.BlogPosts) {
        list.push(itemRenderer(`placeholder-blog-post-${i}`, data.BlogPosts[i]));
      }
    }
  }

  const heading = headingLink ? (
    <Link to={headingLink}>Features &amp; Stories</Link>
  ) : (
    'Features & Stories'
  );
  if (hideIfEmpty && !data?.BlogPosts?.length) {
    return null;
  }
  if (error) {
    return <ErrorRouter error={error} />;
  }
  let header;
  if (category) {
    header = (
      <header className="margin-bottom-20" style={{ marginLeft: gutter, marginRight: gutter }}>
        <p className="blog-post__section-link">
          <Link to={route.blogPosts()} className="link link--energized heading heading--size-32">
            Features &amp; Stories
          </Link>
        </p>
        <Heading
          level={headingLevel}
          className={`heading heading--size-48 ${headingClassName} margin-bottom-10`}
        >
          {category}
        </Heading>
        <div className="dash" />
      </header>
    );
  } else if (tags?.length) {
    header = (
      <header className="margin-bottom-20" style={{ marginLeft: gutter, marginRight: gutter }}>
        <Heading
          withDash={headingWithDash}
          level={headingLevel}
          className={`heading ${headingClassName} margin-bottom-10`}
        >
          {hideHeading ? null : heading}
        </Heading>
        {tags.map(tag => (
          <span
            key={tag}
            className="blog-posts__tag button button--energized button--size-large margin-right-5 margin-bottom-5"
          >
            {tag}{' '}
            <button
              className="blog-posts__cross"
              onClick={() => history.push(route.blogPosts({ tag: tags.filter(t => t !== tag) }))}
            />
          </span>
        ))}
      </header>
    );
  } else {
    header = (
      <header className="margin-bottom-20" style={{ marginLeft: gutter, marginRight: gutter }}>
        <Heading
          withDash={headingWithDash}
          level={headingLevel}
          className={`heading ${headingClassName} margin-bottom-10`}
        >
          {hideHeading ? null : heading}
        </Heading>
      </header>
    );
  }
  return (
    <Component className={className} {...componentProps}>
      {header}
      <Offline
        connecting={loading}
        worksOffline={!!(data && data.BlogPosts && data.BlogPosts.length)}
        error={error}
      >
        {list}
        {!infiniteScroll && showMoreButton ? (
          <div className="blog-posts__footer">
            <Button
              onClick={() => {
                history.push(route.blogPosts());
              }}
              themeType="clear"
              themeName="energized"
              size="large"
            >
              More features &amp; stories
            </Button>
          </div>
        ) : null}
      </Offline>
    </Component>
  );
}

BlogPosts.propTypes = {
  allowHtmlDescription: PropTypes.bool,
  category: PropTypes.string,
  className: PropTypes.string,
  gutter: PropTypes.number,
  headingClassName: PropTypes.string,
  headingLevel: PropTypes.number,
  headingLink: PropTypes.string,
  headingWithDash: PropTypes.bool,
  hideIfEmpty: PropTypes.bool,
  hideHeading: PropTypes.bool,
  infiniteScroll: PropTypes.bool,
  isbn: PropTypes.string,
  isPullable: PropTypes.bool,
  itemProps: PropTypes.shape(ExcerptItemComponent.propTypes),
  limit: PropTypes.number,
  offset: PropTypes.number,
  personId: PropTypes.string,
  showMoreButton: PropTypes.bool,
  skeletonItems: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.string),
};

BlogPosts.defaultProps = {
  allowHtmlDescription: false,
  category: null,
  className: '',
  gutter: 0,
  headingClassName: '',
  headingLevel: 1,
  headingLink: null,
  headingWithDash: false,
  hideIfEmpty: false,
  hideHeading: false,
  infiniteScroll: false,
  isbn: null,
  isPullable: false,
  itemProps: {},
  limit: 20,
  offset: 0,
  personId: null,
  showMoreButton: true,
  skeletonItems: 3,
  tags: null,
};

export default BlogPosts;
